/**
 * API simplificada para Storage - SIN caché, SIN abstracciones
 * Directa, rápida, fácil de entender
 * CON restricción de rutas base por usuario
 */

import type { ListResult, JobStatus } from "../types";
import { API_KEY } from "../config";
import { fetchUserAttributes, fetchAuthSession } from "aws-amplify/auth";

const API_BASE = "https://api.feebbodigital.net/storage";
// const API_BASE = "http://192.168.100.84:3000/development/storage";
const API_TIMEOUT = 12000; // 12s timeout (Lambda = 7s + margen)
const MAX_CONCURRENT_REQUESTS = 3; // Máximo de peticiones simultáneas
const MAX_RETRIES = 3; // Número máximo de reintentos
const RETRY_DELAY_BASE = 500; // Delay base para retry (ms)

// ─── Sistema de permisos por usuario ──────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// NOTA: Sistema de permisos movido al backend
// Los permisos ahora se gestionan desde el backend, por lo que todo este
// código relacionado con USER_PERMISSIONS ha sido comentado.
// ═══════════════════════════════════════════════════════════════════════════

// /**
//  * Configuración de permisos por usuario
//  */
// interface UserPermissions {
//   basePath: string; // Ruta base donde empieza el usuario ("" = raíz)
//   hiddenFolders: string[]; // Carpetas que NO puede ver (rutas relativas a basePath)
// }

// /**
//  * Define permisos para cada usuario según su email
//  *
//  * IMPORTANTE: Si un usuario NO está en esta lista, NO podrá acceder al sistema.
//  *
//  * Configuración:
//  * - basePath: "" = acceso completo desde la raíz (*)
//  * - basePath: "carpeta" = solo puede navegar desde /carpeta (su "Mi unidad")
//  * - hiddenFolders: [] = puede ver todas las carpetas dentro de su basePath
//  * - hiddenFolders: ["2025"] = no puede ver la carpeta 2025 dentro de su basePath
//  */
// const USER_PERMISSIONS: Record<string, UserPermissions> = {
//   // ── ADMINISTRADORES (Acceso completo) ──────────────────────────
//   "javier.atochero@feebbo.com": {
//     basePath: "*", // (*) Acceso completo a todo
//     hiddenFolders: [], // Ve todas las carpetas
//   },
//   "cesar@feebbo.com": {
//     basePath: "*", // (*) Acceso completo a todo
//     hiddenFolders: [],
//   },
//   "manuel.molada@feebbo.com": {
//     basePath: "*", // (*) Acceso completo a todo
//     hiddenFolders: [],
//   },
//   "javier.condado@feebbo.com": {
//     basePath: "DEVS", // Solo ve /DEVS
//     hiddenFolders: [],
//   },

//   // ── USUARIOS CON ACCESO LIMITADO ───────────────────────────────

//   //ADMINISTRACION
//   "compras@feebbo.com": {
//     basePath: "ADMINISTRACION/", // Solo ve /ADMINISTRACION
//     hiddenFolders: [],
//   },
//   "beatriz@businesstrip.es": {
//     basePath: "ADMINISTRACION/", // Solo ve /ADMINISTRACION
//     hiddenFolders: [],
//   },
//   "antonio.lopez@businesstrip.es": {
//     basePath: "ADMINISTRACION/", // Solo ve /ADMINISTRACION
//     hiddenFolders: [],
//   },

//   //ADS
//   "alba.parraga@feebbo.com": {
//     basePath: "FEEBBO/ADS", // Solo ve /ADS
//     hiddenFolders: [],
//   },
//   "raquel.peinador@feebbo.com": {
//     basePath: "FEEBBO/ADS", // Solo ve /ADS
//     hiddenFolders: [],
//   },
//   "joseluis@feebbo.com": {
//     basePath: "FEEBBO/ADS", // Solo ve /ADS
//     hiddenFolders: [],
//   },

//   //AFILIACION
//   "sergio@feebbo.com": {
//     basePath: "FEEBBO/AFILIACION", // Solo ve /AFILIACION
//     hiddenFolders: [],
//   },
//   "joaquin@feebbo.com": {
//     basePath: "FEEBBO/AFILIACION", // Solo ve /AFILIACION
//     hiddenFolders: [],
//   },

//   //COMERCIAL
//   "claudia@feebbo.com": {
//     basePath: "FEEBBO/COMERCIAL", // Solo ve /COMERCIAL
//     hiddenFolders: [],
//   },
//   "virginia@feebbo.com": {
//     basePath: "FEEBBO/COMERCIAL", // Solo ve /COMERCIAL
//     hiddenFolders: [],
//   },
//   "mariajesus@feebbo.com": {
//     basePath: "FEEBBO/COMERCIAL", // Solo ve /COMERCIAL
//     hiddenFolders: [],
//   },

//   // ── EJEMPLOS COMENTADOS ────────────────────────────────────────
//   // Usuario que ve /contratos pero no la carpeta 2025:
//   // "usuario@ejemplo.com": {
//   //   basePath: "contratos",
//   //   hiddenFolders: ["2025"]
//   // },
// };

/**
 * Caché en memoria del email del usuario para evitar llamadas repetidas a Cognito
 */
let _cachedEmail: string | null = null;

/**
 * Obtiene el email del usuario (caché en memoria → localStorage → Cognito)
 * Retorna "" si falla (modo degradado sin restricciones)
 */
async function getUserEmail(): Promise<string> {
  // 1. Caché en memoria (instantáneo)
  if (_cachedEmail !== null) return _cachedEmail;

  // 2. localStorage (rápido)
  try {
    const stored = localStorage.getItem("userEmail");
    if (stored) {
      _cachedEmail = stored.trim().toLowerCase();
      return _cachedEmail;
    }
  } catch (e) {
    console.warn("Error reading localStorage:", e);
  }

  // 3. Cognito (lento, solo 1 vez)
  try {
    const attrs = await Promise.race([fetchUserAttributes(), new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Cognito timeout")), 5000))]);
    const attrsArray = Array.isArray(attrs) ? attrs : Object.entries(attrs).map(([Name, Value]) => ({ Name, Value }));

    const emailAttr = attrsArray.find((a: { Name: string; Value?: string }) => a.Name === "email");

    if (emailAttr?.Value) {
      const email = emailAttr.Value.trim().toLowerCase();
      _cachedEmail = email;
      try {
        localStorage.setItem("userEmail", email);
      } catch (e) {
        console.warn("Error saving to localStorage:", e);
      }
      return email;
    }
  } catch (e) {
    console.warn("⚠️ Error getting user email from Cognito:", e);
    // console.log("📌 Continuing with default permissions (no restrictions)");
  }

  // Modo degradado: sin email = sin restricciones
  _cachedEmail = "";
  return _cachedEmail;
}

/**
 * Obtiene los permisos del usuario actual
 */
// async function getUserPermissions(): Promise<UserPermissions | null> {
//   const email = await getUserEmail();
//   const permissions = USER_PERMISSIONS[email];

//   if (!permissions) {
//     console.error(`❌ Usuario sin permisos configurados: ${email}`);
//     return null;
//   }

//   return permissions;
// }

/**
 * Verifica si el usuario actual tiene acceso al sistema
 * Retorna true si tiene permisos configurados y NO tiene basePath vacío
 */
export async function hasUserAccess(): Promise<{ hasAccess: boolean; email: string; reason?: string }> {
  const email = await getUserEmail();
  // const permissions = USER_PERMISSIONS[email];

  if (!email) {
    return { hasAccess: false, email: "", reason: "No se pudo obtener el email del usuario" };
  }

  // if (!permissions) {
  //   return { hasAccess: false, email, reason: "Usuario no configurado en el sistema" };
  // }

  // Si basePath es "" (vacío), el usuario NO tiene acceso
  // Solo "*" o una ruta específica son válidos
  // if (permissions.basePath === "") {
  //   return { hasAccess: false, email, reason: "Sin permisos de acceso asignados" };
  // }

  return { hasAccess: true, email };
}

// /**
//  * Obtiene la ruta base del usuario actual
//  * DEPRECATED: Ahora se controla desde el backend
//  */
// export async function getUserBasePath(): Promise<string> {
//   const permissions = await getUserPermissions();
//   if (!permissions) {
//     throw new Error("Usuario sin permisos configurados. Contacta con el administrador.");
//   }

//   // Si basePath es "", lanzar error (no tiene acceso)
//   if (permissions.basePath === "") {
//     throw new Error("Sin permisos de acceso. Contacta con el administrador.");
//   }

//   // "*" significa acceso completo (equivalente a "")
//   return permissions.basePath === "*" ? "" : permissions.basePath;
// }

/**
 * Obtiene la ruta base del usuario actual
 * Ahora retorna ruta vacía ya que los permisos se controlan desde el backend
 */
export async function getUserBasePath(): Promise<string> {
  return "";
}

// /**
//  * Obtiene la ruta inicial donde debe empezar el usuario
//  * Útil para inicializar la navegación
//  * DEPRECATED: Ahora se controla desde el backend
//  */
// export async function getInitialPath(): Promise<string> {
//   const permissions = await getUserPermissions();
//   if (!permissions) {
//     throw new Error("Usuario sin permisos configurados. Contacta con el administrador.");
//   }

//   // Si basePath es "", lanzar error (no tiene acceso)
//   if (permissions.basePath === "") {
//     throw new Error("Sin permisos de acceso. Contacta con el administrador.");
//   }

//   // "*" significa acceso completo (equivalente a "")
//   return permissions.basePath === "*" ? "" : permissions.basePath;
// }

/**
 * Obtiene la ruta inicial donde debe empezar el usuario
 * Ahora retorna ruta vacía ya que los permisos se controlan desde el backend
 */
export async function getInitialPath(): Promise<string> {
  return "";
}

// /**
//  * Valida que una ruta esté dentro de la ruta base permitida del usuario
//  * Nunca lanza errores - retorna ruta segura en caso de fallo
//  * DEPRECATED: Ahora se controla desde el backend
//  */
// async function enforceBasePath(requestedPath: string): Promise<string> {
//   try {
//     const basePath = await getUserBasePath();

//     // Si no hay restricción (basePath = ""), permitir cualquier ruta
//     if (!basePath) return requestedPath;

//     const normalized = normalizePath(requestedPath);
//     const normalizedBase = normalizePath(basePath);

//     // Si la ruta solicitada está vacía, usar la ruta base
//     if (!normalized) return normalizedBase;

//     // Verificar que la ruta comience con la ruta base
//     if (!normalized.startsWith(normalizedBase)) {
//       console.warn(`⚠️ Access denied: ${normalized} is outside base path ${normalizedBase}`);
//       return normalizedBase; // Forzar a la ruta base
//     }

//     return normalized;
//   } catch (e) {
//     console.error("❌ Error in enforceBasePath:", e);
//     // Modo degradado: retornar la ruta solicitada sin restricciones
//     return requestedPath;
//   }
// }

/**
 * Valida que una ruta esté dentro de la ruta base permitida del usuario
 * Ahora retorna la ruta normalizada ya que los permisos se controlan desde el backend
 */
async function enforceBasePath(requestedPath: string): Promise<string> {
  return normalizePath(requestedPath);
}

// /**
//  * Verifica si una carpeta está oculta para el usuario actual
//  * DEPRECATED: Ahora se controla desde el backend
//  */
// async function isFolderHidden(folderPath: string): Promise<boolean> {
//   const permissions = await getUserPermissions();

//   // Si no hay permisos, bloquear acceso
//   if (!permissions) return true;

//   // Si no hay carpetas ocultas, todo es visible
//   if (permissions.hiddenFolders.length === 0) return false;

/**
 * Verifica si una carpeta está oculta para el usuario actual
 * Ahora retorna false ya que los permisos se controlan desde el backend
 */
// async function isFolderHidden(folderPath: string): Promise<boolean> {
//   return false;

  // const normalized = normalizePath(folderPath);
  // const basePath = normalizePath(permissions.basePath);

  // // Obtener la ruta relativa a la base del usuario
  // let relativePath = normalized;
  // if (basePath && normalized.startsWith(basePath)) {
  //   relativePath = normalized.substring(basePath.length).replace(/^\//, "");
  // }

  // // Verificar si la carpeta está en la lista de ocultas
  // return permissions.hiddenFolders.some((hidden) => {
  //   const normalizedHidden = normalizePath(hidden);
  //   // Coincidencia exacta o subcarpeta
  //   return relativePath === normalizedHidden || relativePath.startsWith(normalizedHidden + "/");
  // });
// }

// /**
//  * Filtra carpetas ocultas de una lista de resultados
//  * Nunca lanza errores - retorna items sin filtrar en caso de fallo
//  * DEPRECATED: Ahora se controla desde el backend
//  */
// async function filterHiddenFolders(items: any[]): Promise<any[]> {
//   try {
//     const permissions = await getUserPermissions();

//     // Si no hay permisos, bloquear todo
//     if (!permissions) return [];

//     // Si no hay carpetas ocultas, retornar todo
//     if (permissions.hiddenFolders.length === 0) return items;

//     // Filtrar carpetas ocultas
//     const filtered = [];
//     for (const item of items) {
//       const hidden = await isFolderHidden(item.path);
//       if (!hidden) {
//         filtered.push(item);
//       } else {
//         // console.log(`🚫 Hidden folder filtered: ${item.path}`);
//       }
//     }

//     return filtered;
//   } catch (e) {
//     console.error("❌ Error filtering hidden folders:", e);
//     // Modo degradado: retornar todos los items sin filtrar
//     return items;

/**
 * Filtra carpetas ocultas de una lista de resultados
 * Ahora retorna todos los items ya que los permisos se controlan desde el backend
 */
async function filterHiddenFolders(items: any[]): Promise<any[]> {
  return items;
}

// ─── Utilidades ───────────────────────────────────────────────────────
function normalizePath(p: string): string {
  return String(p ?? "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
}

// Asegura que una ruta de carpeta termine con "/"
function ensureTrailingSlash(p: string): string {
  return p.endsWith("/") ? p : `${p}/`;
}

function toApiPrefix(prefix: string): string {
  const norm = normalizePath(prefix);
  return norm ? `${norm}/` : "";
}

/**
 * Detecta si el dispositivo es móvil
 */
export function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;

  // Detectar por user agent
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  // Detectar por tamaño de pantalla
  const isSmallScreen = window.innerWidth <= 768;

  // Detectar por touch support
  const hasTouchScreen = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  return mobileRegex.test(userAgent) || (isSmallScreen && hasTouchScreen);
}

/**
 * Obtiene información del dispositivo para logging
 */
export function getDeviceInfo(): { isMobile: boolean; width: number; height: number; userAgent: string } {
  if (typeof window === "undefined") {
    return { isMobile: false, width: 0, height: 0, userAgent: "" };
  }

  return {
    isMobile: isMobileDevice(),
    width: window.innerWidth,
    height: window.innerHeight,
    userAgent: navigator.userAgent,
  };
}

// ─── Request Queue System ─────────────────────────────────────────────

interface QueuedRequest {
  id: string;
  execute: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (error: any) => void;
  priority: number; // 0 = alta, 1 = normal, 2 = baja
}

class RequestQueue {
  private queue: QueuedRequest[] = [];
  private activeRequests = 0;
  private requestCache = new Map<string, Promise<any>>();

  async enqueue<T>(id: string, execute: () => Promise<T>, priority = 1): Promise<T> {
    // Deduplicación: si ya existe una petición idéntica en curso, reutilizarla
    const cached = this.requestCache.get(id);
    if (cached) {
      // console.log(`♻️ Reusing cached request: ${id}`);
      return cached as Promise<T>;
    }

    return new Promise<T>((resolve, reject) => {
      const request: QueuedRequest = { id, execute, resolve, reject, priority };

      // Insertar en la cola según prioridad
      const insertIndex = this.queue.findIndex((r) => r.priority > priority);
      if (insertIndex === -1) {
        this.queue.push(request);
      } else {
        this.queue.splice(insertIndex, 0, request);
      }

      this.processQueue();
    });
  }

  private async processQueue() {
    if (this.activeRequests >= MAX_CONCURRENT_REQUESTS || this.queue.length === 0) {
      return;
    }

    const request = this.queue.shift();
    if (!request) return;

    this.activeRequests++;
    // console.log(`🚀 Processing request (${this.activeRequests}/${MAX_CONCURRENT_REQUESTS}): ${request.id}`);

    // Crear promesa y cachearla
    const promise = request
      .execute()
      .then((result) => {
        request.resolve(result);
        return result;
      })
      .catch((error) => {
        request.reject(error);
        throw error;
      })
      .finally(() => {
        this.activeRequests--;
        this.requestCache.delete(request.id);
        // console.log(`✅ Request completed (${this.activeRequests}/${MAX_CONCURRENT_REQUESTS}): ${request.id}`);
        this.processQueue();
      });

    this.requestCache.set(request.id, promise);
  }

  clear() {
    this.queue = [];
    this.requestCache.clear();
  }
}

const requestQueue = new RequestQueue();

// ─── Retry logic con exponential backoff ──────────────────────────────

async function withRetry<T>(fn: () => Promise<T>, retries = MAX_RETRIES, delay = RETRY_DELAY_BASE, requestId = "unknown"): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // No reintentar si es AbortError
      if (lastError.name === "AbortError") {
        throw lastError;
      }

      // Reintentar solo en errores 5xx (server errors) o timeouts
      const shouldRetry = lastError.message.includes("HTTP 5") || lastError.message.includes("Timeout") || lastError.message.includes("Internal server error");

      if (!shouldRetry || attempt === retries) {
        throw lastError;
      }

      // Exponential backoff: 1s, 2s, 4s
      const waitTime = delay * Math.pow(2, attempt);
      console.warn(`⚠️ Request failed (attempt ${attempt + 1}/${retries + 1}): ${requestId}`);
      // console.log(`⏳ Retrying in ${waitTime}ms...`);

      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  throw lastError;
}

// ─── Fetch wrapper con timeout ────────────────────────────────────────
async function apiPost<T>(endpoint: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (API_KEY) headers["x-api-key"] = API_KEY;

  // Obtener token de sesión y añadir Authorization header
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    // console.log(token)
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  } catch (e) {
    console.warn("⚠️ No se pudo obtener el token de sesión:", e);
  }

  const requestId = `${endpoint}:${JSON.stringify(body).substring(0, 50)}`;

  const executeRequest = async (): Promise<T> => {
    // Crear AbortController con timeout si no hay signal externo
    const timeoutCtrl = new AbortController();
    const timeout = setTimeout(() => timeoutCtrl.abort(), API_TIMEOUT);

    // Combinar signal externo con timeout
    const combinedSignal = signal ? mergeAbortSignals(signal, timeoutCtrl.signal) : timeoutCtrl.signal;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: combinedSignal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        console.error(`🔴 Error ${res.status}:`, text);
        throw new Error(`HTTP ${res.status}: ${text}`);
      }

      const data = await res.json();
      return data;
    } catch (e) {
      if (timeoutCtrl.signal.aborted && !signal?.aborted) {
        throw new Error(`Timeout: ${endpoint} tardó más de ${API_TIMEOUT / 1000}s`);
      }
      console.error(`❌ Fetch error for ${endpoint}:`, e);
      if (e instanceof Error) {
        throw new Error(`Failed to fetch ${endpoint}: ${e.message}`);
      }
      throw e;
    } finally {
      clearTimeout(timeout);
    }
  };

  // Ejecutar con retry y queue
  return withRetry(() => executeRequest(), MAX_RETRIES, RETRY_DELAY_BASE, requestId);
}

/** Combina dos AbortSignals: aborta cuando cualquiera de los dos aborte */
function mergeAbortSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  const ctrl = new AbortController();
  const abort = () => ctrl.abort();
  if (a.aborted || b.aborted) {
    ctrl.abort();
    return ctrl.signal;
  }
  a.addEventListener("abort", abort, { once: true });
  b.addEventListener("abort", abort, { once: true });
  return ctrl.signal;
}

// ═══════════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════

// ─── Queue Management ─────────────────────────────────────────────────

/**
 * Limpia la cola de peticiones (útil al cerrar sesión o resetear)
 */
export function clearRequestQueue() {
  requestQueue.clear();
}

// ─── List Files ────────────────────────────────────────────────────────

/**
 * Lista archivos y carpetas con soporte de cola y prioridad
 * @param prefix Ruta a listar
 * @param maxKeys Máximo de elementos a retornar
 * @param signal AbortSignal para cancelar la petición
 * @param priority 0=alta (navegación usuario), 1=normal, 2=baja (tree lazy-load)
 */
export async function listFiles(prefix: string, maxKeys = 100, signal?: AbortSignal, priority = 1): Promise<ListResult> {
  // Aplicar restricción de ruta base del usuario
  const enforcedPrefix = await enforceBasePath(prefix);
  const apiPrefix = toApiPrefix(enforcedPrefix);

  // console.log("📁 Listing files:", {
  //   requested: prefix,
  //   enforced: enforcedPrefix,
  //   apiPrefix,
  //   maxKeys,
  //   priority: priority === 0 ? 'HIGH' : priority === 1 ? 'NORMAL' : 'LOW'
  // });

  // ID único para deduplicación
  const requestId = `listFiles:${apiPrefix}:${maxKeys}`;

  try {
    // Encolar petición con prioridad
    const data = await requestQueue.enqueue(requestId, () => apiPost<any>("/v1/files/list", { prefix: apiPrefix, maxKeys }, signal), priority);

    // Verificar si la petición fue abortada mientras estaba en cola
    if (signal?.aborted) {
      throw new Error("AbortError");
    }

    // Normalizar carpetas con "/" final para distinguirlas de archivos
    const folders = (data.folders ?? []).map((f: any) => ({ ...f, path: ensureTrailingSlash(normalizePath(f.path)) }));
    const files = (data.files ?? []).map((f: any) => ({ ...f, path: normalizePath(f.path) }));

    // Filtrar carpetas ocultas
    const visibleFolders = await filterHiddenFolders(folders);

    // Filtrar archivos .zip en la raíz (prefix vacío o solo basePath del usuario)
    const visibleFiles = files.filter((f: any) => {
      const isRootLevel = !enforcedPrefix || enforcedPrefix === "";
      const isZipFile = f.name?.toLowerCase().endsWith(".zip");

      // Ocultar .zip solo si estamos en la raíz
      if (isRootLevel && isZipFile) {
        return false;
      }
      return true;
    });

    return {
      folders: visibleFolders,
      files: visibleFiles,
      isTruncated: data.isTruncated ?? false,
      nextToken: data.nextToken ?? null,
    };
  } catch (e) {
    // Si es un AbortError, propagar sin loguear (es intencional)
    if (e instanceof Error && e.name === "AbortError") {
      throw e;
    }

    console.error("❌ listFiles error:", e);

    // Para otros errores, lanzar con mensaje más descriptivo
    const errorMsg = e instanceof Error ? e.message : String(e);

    // Detectar error de permisos insuficientes
    if (errorMsg.includes("403") || errorMsg.includes("Forbidden") || errorMsg.includes("permission") || errorMsg.toLowerCase().includes("permiso")) {
      throw new Error("No tienes permisos suficientes. Debes estar asignado a un grupo para acceder a este sistema.");
    }

    throw new Error(`Error al listar archivos: ${errorMsg}`);
  }
}

// ─── Create Folder ─────────────────────────────────────────────────────
export async function createFolder(path: string): Promise<void> {
  await apiPost("/v1/folders/create", { path });
}

// ─── Upload ────────────────────────────────────────────────────────
export async function getUploadUrl(name: string, type: string, directory: string): Promise<{ uploadURL: string; key: string }> {
  return apiPost("/v1/files/upload-url", { name, type, directory });
}

export function uploadToUrl(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    });

    xhr.addEventListener("error", () => reject(new Error("Network error")));

    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.send(file);
  });
}

// ─── Download by private link ──────────────────────────────────────────
/**
 * Descarga directa de un fichero desde un enlace privado.
 * Llama a GET /v1/files/links/{linkId} con Authorization Bearer y dispara
 * la descarga en el navegador sin abrir pestañas ni renderizar UI.
 *
 * @throws Error con .status === 401 si no hay sesión / token inválido.
 */
export async function downloadFileByLink(linkId: string): Promise<void> {
  const url = `${API_BASE}/v1/files/links/${encodeURIComponent(linkId)}`;
  const headers: Record<string, string> = {};
  if (API_KEY) headers["x-api-key"] = API_KEY;

  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    const err: any = new Error("No auth session");
    err.status = 401;
    throw err;
  }
  headers["Authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(url, { method: "GET", headers });
  } catch (e) {
    console.error("❌ Fetch falló (red/CORS):", e);
    console.groupEnd();
    throw e;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    console.error("❌ Body de error:", text);
    console.groupEnd();
    let parsed: any = null;
    try { parsed = JSON.parse(text); } catch {}
    const err: any = new Error(parsed?.message || text || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = parsed?.code;
    err.retryAfter = parsed?.retryAfter;
    err.raw = text;
    throw err;
  }

  // El API ahora devuelve JSON con downloadUrl en lugar del archivo directo
  const data = await res.json();
  
  if (data.status === "OK" && data.downloadUrl) {
    // Simular click en el downloadUrl (presigned S3 URL)
    const a = document.createElement("a");
    a.href = data.downloadUrl;
    a.download = data.fileName || linkId;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return;
  }

  // Fallback al comportamiento anterior si no hay downloadUrl
  let filename = linkId;
  const cd = res.headers.get("content-disposition") || res.headers.get("Content-Disposition");
  if (cd) {
    const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const plainMatch = cd.match(/filename="?([^";]+)"?/i);
    if (utf8Match?.[1]) filename = decodeURIComponent(utf8Match[1]);
    else if (plainMatch?.[1]) filename = plainMatch[1];
  }

  const blob = await res.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const b = document.createElement("a");
  b.href = blobUrl;
  b.download = filename;
  document.body.appendChild(b);
  b.click();
  document.body.removeChild(b);
  window.URL.revokeObjectURL(blobUrl);
}

// ─── Download ──────────────────────────────────────────────────────────
export async function getDownloadUrl(key: string, forceDownload: boolean = true): Promise<string> {
  const response = await apiPost<any>("/v1/files/download-url", {
    key,
    forceDownload,
  });

  // El backend devuelve { status: "OK", downloadURL: "..." }
  return response.downloadURL || response.url || response;
}

export async function getDownloadUrls(keys: string[], forceDownload: boolean = true): Promise<{ key: string; downloadURL: string }[]> {
  // Si es un solo archivo
  if (keys.length === 1) {
    const data = await apiPost<any>("/v1/files/download-url", {
      key: keys[0],
      forceDownload,
    });
    return [{ key: keys[0], downloadURL: data.downloadURL }];
  }

  // Si son múltiples, hacer peticiones individuales
  const results = await Promise.all(
    keys.map(async (key) => {
      const data = await apiPost<any>("/v1/files/download-url", {
        key,
        forceDownload,
      });
      return { key, downloadURL: data.downloadURL };
    }),
  );

  return results;
}

// ─── Delete ────────────────────────────────────────────────────────────
export async function deleteFiles(keys: string[]): Promise<void> {
  // Normalizar rutas: archivos sin /, carpetas con /
  const normalizedKeys = keys.map((k) => {
    const apiPath = toApiPrefix(k);

    // Detectar si es carpeta (sin extensión o termina con /)
    const hasExtension = k.split("/").pop()?.includes(".") ?? false;
    const isZipFile = k.toLowerCase().endsWith(".zip") || k.toLowerCase().endsWith(".zip/");
    const endsWithSlash = k.endsWith("/");

    // Es carpeta si: termina con / O no tiene extensión (excepto .zip)
    const isFolder = (endsWithSlash || !hasExtension) && !isZipFile;

    // Normalizar: carpetas con /, archivos sin /
    let normalizedPath = apiPath;
    if (isFolder && !apiPath.endsWith("/")) {
      normalizedPath = apiPath + "/";
    } else if (!isFolder && apiPath.endsWith("/")) {
      normalizedPath = apiPath.slice(0, -1);
    }

    return normalizedPath;
  });

  await apiPost("/v1/files/delete", { keys: normalizedKeys });
}

// ─── Move / Rename ─────────────────────────────────────────────────────
export interface MoveItem {
  type: "file" | "folder";
  path: string;
  destination: string;
}

export async function moveFiles(items: MoveItem[]): Promise<MoveResult> {
  console.log("🔄 Move API call:", { items });
  const data = await apiPost<any>("/v1/files/move", { items });
  console.log("✅ Move API response:", data);

  // Si la respuesta tiene jobId, es un job asíncrono
  if (data.jobId) {
    return { status: "processing", jobId: data.jobId };
  }

  // Si no, es una respuesta síncrona completa
  return data;
}

export interface MoveResult {
  status: string;
  moved?: { path: string; destination: string }[];
  errors?: { path: string; destination: string; error: string }[];
  totalMoved?: number;
  totalErrors?: number;
  jobId?: string; // para operaciones asíncronas
}

// ─── Download ZIP ──────────────────────────────────────────────────────
/**
 * Descarga una o múltiples rutas como archivo ZIP
 * @param paths - Ruta única (string) o múltiples rutas (string[])
 * @returns URL de descarga directa o jobId para polling
 */
export async function downloadAsZip(paths: string | string[]): Promise<{ jobId: string; totalFiles: number }> {
  const pathArray = Array.isArray(paths) ? paths : [paths];

  if (pathArray.length === 0) {
    throw new Error("No se proporcionaron rutas para descargar");
  }

  // Convertir rutas a formato de items con type y path
  const items = pathArray.map((p) => {
    // Normalizar ruta (quitar / iniciales y finales)
    const normalizedBase = normalizePath(p);

    // Detectar si es carpeta (sin extensión o termina con /)
    const hasExtension = p.split("/").pop()?.includes(".") ?? false;
    const isZipFile = p.toLowerCase().endsWith(".zip") || p.toLowerCase().endsWith(".zip/");
    const endsWithSlash = p.endsWith("/");

    // Es carpeta si: termina con / O no tiene extensión (excepto .zip)
    const isFolder = (endsWithSlash || !hasExtension) && !isZipFile;

    // Normalizar: carpetas con /, archivos sin /
    let normalizedPath = normalizedBase;
    if (isFolder && !normalizedPath.endsWith("/")) {
      normalizedPath = normalizedPath + "/";
    } else if (!isFolder && normalizedPath.endsWith("/")) {
      normalizedPath = normalizedPath.slice(0, -1);
    }

    return {
      type: isFolder ? "folder" : "file",
      path: normalizedPath,
    };
  });

  const payload = { items };

  const data = await apiPost<any>("/v1/folders/download-folder-zip", payload);

  // La API devuelve: { status: "ACCEPTED", message: "...", jobId: "...", totalFiles: N }
  return { jobId: data.jobId as string, totalFiles: (data.totalFiles || 0) as number };
}

// Alias para compatibilidad con código existente
export const downloadZip = downloadAsZip;
export const downloadFolderZip = downloadAsZip;
export const downloadMultipleAsZip = downloadAsZip;

// ─── Share Links (permanente / temporal) ───────────────────────────────

export interface FileLinkResult {
  linkId: string;
  url: string;
  path: string;
  expiresAt: string | null;
}

/**
 * Crea un enlace para compartir un archivo.
 * - Si no se pasa `expiresInDays` (o es null/0), el link es permanente y
 *   requiere autenticación al abrirse (redirige a login si procede).
 * - Si se pasa `expiresInDays > 0`, el link es temporal y público
 *   (accesible sin autenticación hasta que expire).
 *
 * @param path Ruta del archivo en S3 (sin "/" inicial)
 * @param expiresInDays Días hasta expiración (null = permanente)
 */
export async function createFileLink(path: string, expiresInDays: number | null = null): Promise<FileLinkResult> {
  const body: Record<string, unknown> = { path: normalizePath(path) };
  if (expiresInDays && expiresInDays > 0) {
    body.expiresInDays = expiresInDays;
  }
  const data = await apiPost<any>("/v1/files/links/create", body);
  return {
    linkId: data.linkId,
    url: data.url,
    path: data.path,
    expiresAt: data.expiresAt ?? null,
  };
}

// ─── Public Links Management ───────────────────────────────────────────

export interface PublicLink {
  id: string;
  publicUrl: string;
  current_path: string;
  original_name: string;
  file_size: number;
  content_type: string;
  expires_at: string | null;
  is_active: boolean;
  access_count: number;
  last_accessed_at: string | null;
  created_at: string;
  created_by?: string;
  created_by_email?: string;
}

/**
 * Lista todos los enlaces públicos creados.
 * Endpoint GET con x-api-key + Authorization Bearer (igual que el resto).
 */
export async function listPublicLinks(): Promise<PublicLink[]> {
  const url = `${API_BASE}/v1/files/public-links/list`;
  const headers: Record<string, string> = {};
  if (API_KEY) headers["x-api-key"] = API_KEY;

  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch (e) {
    console.warn("⚠️ No se pudo obtener el token de sesión:", e);
  }

  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort(), API_TIMEOUT);
  try {
    const res = await fetch(url, { method: "GET", headers, signal: ctrl.signal });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new Error(`HTTP ${res.status}: ${text}`);
    }
    const data = await res.json();
    return data.links ?? [];
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Desactiva un enlace público por su linkId.
 */
export async function deactivatePublicLink(linkId: string): Promise<void> {
  await apiPost("/v1/files/public-links/deactivate", { linkId });
}

// ─── Job Status ────────────────────────────────────────────────────────
export async function getJobStatus(jobId: string): Promise<JobStatus> {
  const data = await apiPost<any>("/v1/jobs/status", { jobId });
  // La API devuelve: { status: "OK", job: { jobId, status, phase, total, processed, ... } }
  return data.job;
}

const POLL_MAX_ATTEMPTS = 60; // 60 * 5s = 5 min máximo

export async function pollJob(jobId: string, onProgress: (pct: number, phase?: string) => void, intervalMs = 3000): Promise<JobStatus> {
  // Función que consulta el estado una vez
  // Devuelve: JobStatus si terminó, null si en progreso, null si 404 (aún no registrado)
  const checkOnce = async (): Promise<JobStatus | null> => {
    try {
      const job = await getJobStatus(jobId);
      const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
      onProgress(pct, job.phase);

      if (job.status !== "in_progress") {
        return job; // Terminado
      }
      return null; // Aún en progreso
    } catch (e) {
      // Si es 404 = job aún no registrado por el worker, seguir intentando
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("404") || msg.includes("not found") || msg.includes("Job not found")) {
        return null;
      }
      throw e; // Otros errores sí los propagamos
    }
  };

  // Esperar 5s antes del primer check (dar tiempo al worker async)
  await new Promise((r) => setTimeout(r, 5000));

  // Polling cada intervalMs
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const poll = async () => {
      attempts++;
      if (attempts > POLL_MAX_ATTEMPTS) {
        reject(new Error(`Job ${jobId} excedió el tiempo máximo (${(POLL_MAX_ATTEMPTS * intervalMs) / 1000}s)`));
        return;
      }
      try {
        const result = await checkOnce();
        if (result) {
          resolve(result);
        } else {
          setTimeout(poll, intervalMs);
        }
      } catch (e) {
        reject(e);
      }
    };

    poll();
  });
}
